import React, { useState, useEffect, useCallback } from "react";
import { format, differenceInDays } from "date-fns";
import { useBikeStore } from "../../store/useBikeStore";
import { apiService } from "../../services/api";
import { SingleBikeSelector } from "./SingleBikeSelector";
import { PaginationControls } from "./PaginationControls";
import type { GpsCoordinate, GpsListResponse, BikeInfo } from "../../types/gps";

export const GpsDataViewer: React.FC = () => {
  const [gpsData, setGpsData] = useState<GpsCoordinate[]>([]);
  const [selectedBike, setSelectedBike] = useState<string | null>(null);
  const [bikeInfo, setBikeInfo] = useState<BikeInfo | null>(null);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [pageInput, setPageInput] = useState<string>("");

  const { bikes, loadIfNeeded } = useBikeStore();

  useEffect(() => {
    loadIfNeeded().catch(console.error);
  }, [loadIfNeeded]);

  const fetchGpsData = useCallback(async () => {
    if (!selectedBike || !startDate || !endDate) {
      setError("Please select a bike and date range");
      return;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);
    const daysDiff = differenceInDays(end, start);

    if (daysDiff > 7) {
      setError("Date range cannot exceed 7 days");
      return;
    }

    if (daysDiff < 0) {
      setError("End date must be after start date");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const params = {
        bike_id: selectedBike,
        start_time: new Date(startDate).toISOString(),
        end_time: new Date(endDate).toISOString(),
        limit: itemsPerPage,
        offset: (currentPage - 1) * itemsPerPage,
      };

      const data = (await apiService.getGpsCoordinates(
        params,
      )) as GpsListResponse;
      setGpsData(data.coordinates);
      setTotal(data.pagination.total);
      setSelectedItems(new Set());
    } catch (error) {
      console.error("Error fetching GPS data:", error);
      setError("Failed to load GPS data");
    } finally {
      setIsLoading(false);
    }
  }, [selectedBike, startDate, endDate, itemsPerPage, currentPage]);

  // Refetch GPS data when current page or items per page changes (only if we already have data)
  useEffect(() => {
    if (gpsData.length > 0 && selectedBike && startDate && endDate) {
      fetchGpsData();
    }
  }, [currentPage, itemsPerPage, fetchGpsData]);

  // Keep page input in sync with current page
  useEffect(() => {
    if (gpsData.length > 0) {
      setPageInput(currentPage.toString());
    }
  }, [currentPage, gpsData.length]);

  const handleBikeSelect = (bikeId: string | null) => {
    setSelectedBike(bikeId);
    setGpsData([]);
    setTotal(0);
    setCurrentPage(1);
    setSelectedItems(new Set());

    if (bikeId) {
      const bike = bikes.find((b) => b.bike_id === bikeId);
      if (bike) {
        setBikeInfo({
          bike_id: bike.bike_id,
          status: bike.status,
          created_at: bike.created_at,
        });
      }
    } else {
      setBikeInfo(null);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedItems(new Set(gpsData.map((item) => item.id)));
    } else {
      setSelectedItems(new Set());
    }
  };

  const handleSelectItem = (id: string, checked: boolean) => {
    const newSelected = new Set(selectedItems);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedItems(newSelected);
  };

  const handleDeleteSelected = async () => {
    if (selectedItems.size === 0) return;

    if (
      !confirm(
        `Are you sure you want to delete ${selectedItems.size} GPS records?`,
      )
    ) {
      return;
    }

    setIsLoading(true);
    try {
      await apiService.bulkDeleteGpsCoordinates(Array.from(selectedItems));
      // Remove deleted items from state instead of refetching
      const deletedIds = Array.from(selectedItems);
      setGpsData((prevData) =>
        prevData.filter((item) => !deletedIds.includes(item.id)),
      );
      setTotal((prevTotal) => prevTotal - deletedIds.length);
      setSelectedItems(new Set());
      fetchGpsData();
    } catch (error) {
      console.error("Error deleting GPS records:", error);
      setError("Failed to delete GPS records");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteItem = async (id: string) => {
    if (!confirm("Are you sure you want to delete this GPS record?")) {
      return;
    }

    setIsLoading(true);
    try {
      await apiService.deleteGpsCoordinate(id);
      // Remove deleted item from state instead of refetching
      setGpsData((prevData) => prevData.filter((item) => item.id !== id));
      setTotal((prevTotal) => prevTotal - 1);
      setSelectedItems((prevSelected) => {
        const newSelected = new Set(prevSelected);
        newSelected.delete(id);
        return newSelected;
      });
    } catch (error) {
      console.error("Error deleting GPS record:", error);
      setError("Failed to delete GPS record");
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartDateChange = (newStartDate: string) => {
    setStartDate(newStartDate);

    // If end date is not selected, auto-select end date within 7-day limit
    // if (!endDate && newStartDate) {
    //   const startDateObj = new Date(newStartDate);
    //   const autoEndDate = addDays(startDateObj, 7);
    //   setEndDate(format(autoEndDate, "yyyy-MM-dd"));
    // }
  };

  const handleEndDateChange = (newEndDate: string) => {
    setEndDate(newEndDate);

    // If start date is not selected, auto-select start date within 7-day limit
    // if (!startDate && newEndDate) {
    //   const endDateObj = new Date(newEndDate);
    //   const autoStartDate = subDays(endDateObj, 7);
    //   setStartDate(format(autoStartDate, "yyyy-MM-dd"));
    // }
  };

  const formatLocation = (lat: number | string, lon: number | string) => {
    const latitude = typeof lat === "string" ? parseFloat(lat) : lat;
    const longitude = typeof lon === "string" ? parseFloat(lon) : lon;

    if (isNaN(latitude) || isNaN(longitude)) {
      return "Invalid coordinates";
    }

    return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
  };

  const totalPages = Math.ceil(total / itemsPerPage);

  const handlePageInputSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const pageNumber = parseInt(pageInput);

    if (!pageInput.trim()) return;

    if (isNaN(pageNumber) || pageNumber < 1 || pageNumber > totalPages) {
      setError(`Please enter a valid page number between 1 and ${totalPages}`);
      setPageInput(currentPage.toString());
      return;
    }

    setCurrentPage(pageNumber);
    setPageInput(pageNumber.toString());
    setError(null);
  };

  const goToFirstPage = () => {
    setCurrentPage(1);
    setPageInput("1");
  };

  const goToLastPage = () => {
    setCurrentPage(totalPages);
    setPageInput(totalPages.toString());
  };

  const goToPage = (page: number) => {
    setCurrentPage(page);
    setPageInput(page.toString());
  };

  const goToPrevious = () => {
    const newPage = Math.max(1, currentPage - 1);
    setCurrentPage(newPage);
    setPageInput(newPage.toString());
  };

  const goToNext = () => {
    const newPage = Math.min(totalPages, currentPage + 1);
    setCurrentPage(newPage);
    setPageInput(newPage.toString());
  };

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">
          GPS Data Viewer
        </h1>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Bike
            </label>
            <SingleBikeSelector
              bikes={bikes}
              selectedBike={selectedBike}
              onChange={handleBikeSelect}
              placeholder="Choose a bike..."
            />
          </div>

          <div>
            <label
              htmlFor="start-date"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Start Date
            </label>
            <input
              type="date"
              id="start-date"
              value={startDate}
              onChange={(e) => handleStartDateChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label
              htmlFor="end-date"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              End Date
            </label>
            <input
              type="date"
              id="end-date"
              value={endDate}
              onChange={(e) => handleEndDateChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={fetchGpsData}
              disabled={!selectedBike || !startDate || !endDate || isLoading}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? "Loading..." : "View Data"}
            </button>
          </div>
        </div>

        {bikeInfo && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
            <h3 className="text-sm font-medium text-blue-900 mb-2">
              Bike Information
            </h3>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-blue-700 font-medium">Bike ID:</span>
                <span className="ml-2 text-blue-900">{bikeInfo.bike_id}</span>
              </div>
              <div>
                <span className="text-blue-700 font-medium">Status:</span>
                <span className="ml-2 text-blue-900">{bikeInfo.status}</span>
              </div>
              <div>
                <span className="text-blue-700 font-medium">Registered:</span>
                <span className="ml-2 text-blue-900">
                  {format(new Date(bikeInfo.created_at), "MMM dd, yyyy")}
                </span>
              </div>
            </div>
          </div>
        )}

        {gpsData.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-700">
                  Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                  {Math.min(currentPage * itemsPerPage, total)} of {total}{" "}
                  records
                </span>
                <div className="flex items-center space-x-2">
                  <label
                    htmlFor="items-per-page"
                    className="text-sm text-gray-700"
                  >
                    Items per page:
                  </label>
                  <select
                    id="items-per-page"
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                      setPageInput("1");
                    }}
                    className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>

              {selectedItems.size > 0 && (
                <button
                  onClick={handleDeleteSelected}
                  disabled={isLoading}
                  className="px-4 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50"
                >
                  Delete Selected ({selectedItems.size})
                </button>
              )}
            </div>

            {/* Top pagination */}
            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              isLoading={isLoading}
              pageInput={pageInput}
              onPageInputChange={setPageInput}
              onPageInputSubmit={handlePageInputSubmit}
              onFirstPage={goToFirstPage}
              onLastPage={goToLastPage}
              onPrevious={goToPrevious}
              onNext={goToNext}
              onGoToPage={goToPage}
            />

            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={
                          selectedItems.size === gpsData.length &&
                          gpsData.length > 0
                        }
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Timestamp
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Location
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Speed
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Battery
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {gpsData.map((record) => (
                    <tr key={record.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedItems.has(record.id)}
                          onChange={(e) =>
                            handleSelectItem(record.id, e.target.checked)
                          }
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {format(
                          new Date(record.timestamp),
                          "MMM dd, yyyy HH:mm:ss",
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatLocation(record.latitude, record.longitude)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {record.speed !== undefined
                          ? `${record.speed} km/h`
                          : "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {record.battery_level !== undefined
                          ? `${record.battery_level}%`
                          : "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => handleDeleteItem(record.id)}
                          disabled={isLoading}
                          className="text-red-600 hover:text-red-900 disabled:opacity-50"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Bottom pagination */}
            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              isLoading={isLoading}
              pageInput={pageInput}
              onPageInputChange={setPageInput}
              onPageInputSubmit={handlePageInputSubmit}
              onFirstPage={goToFirstPage}
              onLastPage={goToLastPage}
              onPrevious={goToPrevious}
              onNext={goToNext}
              onGoToPage={goToPage}
            />
          </div>
        )}

        {gpsData.length === 0 &&
          !isLoading &&
          selectedBike &&
          startDate &&
          endDate && (
            <div className="text-center py-8">
              <p className="text-gray-500">
                No GPS data found for the selected filters.
              </p>
            </div>
          )}
      </div>
    </div>
  );
};
